/**
 * Parse API error response and return user-friendly message
 */
export async function parseApiError(response: Response): Promise<string> {
  let result;
  try {
    result = await response.json();
  } catch {
    return `Server error: ${response.status} ${response.statusText}`;
  }
  return result.error || `Request failed: ${response.status}`;
}

const NUMERIC_STRING_PATTERN = /^-?\d+(?:\.\d+)?$/;

/**
 * Recursively convert numeric strings in API payloads to real numbers.
 */
export function normalizeApiNumbers<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeApiNumbers(item)) as T;
  }

  if (value && typeof value === "object") {
    const normalizedEntries = Object.entries(
      value as Record<string, unknown>,
    ).map(([key, entryValue]) => [key, normalizeApiNumbers(entryValue)]);
    return Object.fromEntries(normalizedEntries) as T;
  }

  if (typeof value === "string" && NUMERIC_STRING_PATTERN.test(value.trim())) {
    return Number(value) as T;
  }

  return value;
}

export async function parseApiJson<T>(response: Response): Promise<T> {
  const raw = await response.json();
  return normalizeApiNumbers(raw) as T;
}

interface UploadFileApiResponse {
  success: boolean;
  file?: {
    path?: string;
  };
  error?: string;
  details?: {
    php_upload_max_filesize?: string;
    php_post_max_size?: string;
    app_limit_bytes?: number;
    request_content_length?: number;
  };
}

/**
 * Resize and convert uploaded images to WebP before upload.
 * Skips GIF files to preserve animations.
 */
async function compressImageForUpload(file: File): Promise<File> {
  const isImage = file.type.startsWith("image/");
  if (!isImage || file.type === "image/gif" || typeof window === "undefined") {
    return file;
  }

  const MAX_DIMENSION = 1920;
  const WEBP_QUALITY = 0.82;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to decode image"));
      img.src = objectUrl;
    });

    const ratio = Math.min(
      MAX_DIMENSION / image.width,
      MAX_DIMENSION / image.height,
      1,
    );
    const targetWidth = Math.max(1, Math.round(image.width * ratio));
    const targetHeight = Math.max(1, Math.round(image.height * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", WEBP_QUALITY);
    });

    if (!blob) {
      return file;
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "") || "image";
    return new File([blob], `${baseName}.webp`, {
      type: "image/webp",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Handle API request with common error handling
 */
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorMessage = await parseApiError(response);
    throw new Error(errorMessage);
  }

  try {
    return await parseApiJson<T>(response);
  } catch {
    throw new Error("Invalid response from server");
  }
}

/**
 * Upload a file to the server
 */
export async function uploadFile(
  file: File,
  type: "program" | "image",
): Promise<string> {
  const fileToUpload =
    type === "image" ? await compressImageForUpload(file) : file;
  const formData = new FormData();
  formData.append("file", fileToUpload);
  formData.append("type", type);

  // Get token from localStorage (same key as AuthContext uses)
  const token = localStorage.getItem("travel_engine_token");

  // Build fetch options
  const fetchOptions: RequestInit = {
    method: "POST",
    credentials: "include",
    body: formData,
  };

  // Add Authorization header if token exists
  if (token) {
    fetchOptions.headers = {
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await fetch("/api/upload-file.php", fetchOptions);

  // Get response text first for debugging
  const responseText = await response.text();

  if (!response.ok) {
    try {
      const result = JSON.parse(responseText);
      const diagnostics = result.details
        ? ` [php upload_max_filesize=${result.details.php_upload_max_filesize}, php post_max_size=${result.details.php_post_max_size}, app_limit_bytes=${result.details.app_limit_bytes}, request_content_length=${result.details.request_content_length}]`
        : "";
      throw new Error(
        (result.error || `Request failed: ${response.status}`) + diagnostics,
      );
    } catch (e) {
      throw new Error(
        `Server error: ${response.status} - ${responseText.substring(0, 100)}`,
      );
    }
  }

  let result: UploadFileApiResponse;
  try {
    result = JSON.parse(responseText);
  } catch (e) {
    console.error("Failed to parse JSON:", responseText);
    throw new Error("Invalid response from server");
  }

  if (!result.success || !result.file?.path) {
    throw new Error("Upload failed");
  }

  return result.file.path;
}

/**
 * Transform form data to API format for trip creation/update
 */
export interface TripFormData {
  title: string;
  destination: string;
  subtitle: string;
  duration: string;
  startDate: string;
  endDate: string;
  description: string;
  extraInfo: string;
  price: string;
  priceFrom?: boolean;
  difficulty: string;
  status: string;
  extraSinglePrice: string;
  images: string;
  highlights: string;
  included: string;
  notIncluded: string;
  category: string;
  programUrl: string;
  itinerary: Array<{ day: number; title: string; description: string }>;
  faq: Array<{ question: string; answer: string }>;
  customInfo: Array<{ title: string; text: string }>;
}

export function transformFormDataToApi(formData: TripFormData, id?: number) {
  const isSpecialCategory = formData.category === "special";
  const toNullableInt = (value: string) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const toNullableFloat = (value: string) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const data: any = {
    title: formData.title,
    destination:
      formData.destination && formData.destination.trim() !== ""
        ? formData.destination
        : isSpecialCategory
          ? null
          : "",
    subtitle:
      formData.subtitle && formData.subtitle.trim() !== ""
        ? formData.subtitle
        : isSpecialCategory
          ? null
          : "",
    duration: toNullableInt(formData.duration),
    start_date: formData.startDate || null,
    end_date: formData.endDate || null,
    description:
      formData.description && formData.description.trim() !== ""
        ? formData.description
        : null,
    extra_info:
      formData.extraInfo && formData.extraInfo.trim() !== ""
        ? formData.extraInfo
        : null,
    price: toNullableFloat(formData.price),
    price_from: formData.priceFrom || false,
    difficulty:
      formData.difficulty && formData.difficulty.trim() !== ""
        ? formData.difficulty
        : isSpecialCategory
          ? null
          : "Moderate",
    status: formData.status || null,
    extra_single_price: toNullableFloat(formData.extraSinglePrice),
    images: formData.images
      .split(",")
      .map((img) => img.trim())
      .filter(Boolean),
    highlights: formData.highlights
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean),
    included: formData.included
      .split(",")
      .map((i) => i.trim())
      .filter(Boolean),
    not_included: formData.notIncluded
      .split(",")
      .map((i) => i.trim())
      .filter(Boolean),
    category: formData.category || "General",
    program_url: formData.programUrl || null,
    itinerary: formData.itinerary,
    faq: formData.faq,
    custom_info: formData.customInfo,
  };

  if (id !== undefined) {
    data.id = id;
  }

  return data;
}
